/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';

import { Form, Grid } from '@mairistem/composants';

import { AssistantContext } from '@/contexts';

import { Steps } from '@/enums';
import { sexeToSexeCode } from '@/utils';
import InformationsNaissance from './InformationsNaissance';
import InformationsContact from './InformationsContact';
import InformationsComplementaires from './InformationsComplementaires';

interface StepPartenaireProps {
  numeroPartenaire: number;
}

const StepPartenaire = (props: StepPartenaireProps) => {
  const { numeroPartenaire } = props;

  const {
    conventionDeclaration,
    complementInsee: {
      nombreEnfants,
      p1DejaPacse,
      p2DejaPacse,
      p1EtatMatrimonialCode,
      p2EtatMatrimonialCode,
      p1DateDivorce,
      p2DateDivorce,
      p1DateVeuvage,
      p2DateVeuvage,
    },
    onChange,
    errors,
    selectedDeclarationType,
  } = React.useContext(AssistantContext);

  // Projet de PACS : seul le nom des partenaires est obligatoire
  const isProjet = selectedDeclarationType === 'projet';

  // Sélectionner les valeurs correspondantes au numéro du partenaire
  const dejaPacse = numeroPartenaire === 1 ? p1DejaPacse : p2DejaPacse;
  const etatMatrimonialCode =
    numeroPartenaire === 1 ? p1EtatMatrimonialCode : p2EtatMatrimonialCode;

  // Récupérer selon numeroPartenaire en props (1 ou 2)
  const partenaireKey = `partenaire${numeroPartenaire}` as const;
  const partenaireData = React.useMemo(
    () => conventionDeclaration?.[partenaireKey],
    [conventionDeclaration, partenaireKey],
  );
  const {
    tel1,
    tel2,
    mail,
    protectionOfpra,
    protectionJuridique,
    identiteVerifiee,
    adresseResidence,
  } = partenaireData ?? {};

  const residenceCommuneDelegue = adresseResidence?.residenceCommuneDelegue;
  const residenceLibelleCommuneDelegue =
    adresseResidence?.residenceLibelleCommuneDelegue;

  const partenaire = partenaireData ?? {};

  const onChangePartenaire = React.useCallback(
    (_e, data) => {
      if (!data?.name) {
        onChange(_e, data);
        return;
      }

      const isAlreadyMapped =
        data.name.startsWith(`conventionDeclaration.${partenaireKey}.`) ||
        data.name.startsWith('complementInsee.') ||
        data.name.startsWith('conventionDeclaration.');
      const mappedName = isAlreadyMapped
        ? data.name
        : `conventionDeclaration.${partenaireKey}.${data.name}`;

      onChange(_e, {
        ...data,
        name: mappedName,
      });

      // EtatCivil ne gérant que .sexe, on re-calcul sexeCode pour
      // rester synchroniser
      if (data.name === 'sexe') {
        onChange(_e, {
          name: `conventionDeclaration.${partenaireKey}.sexeCode`,
          value: sexeToSexeCode(data.value),
        });
      }
    },
    [onChange, partenaireKey],
  );

  const errorsStep =
    errors?.[numeroPartenaire === 1 ? Steps.Partenaire1 : Steps.Partenaire2] ||
    {};

  return (
    <Grid padded as={Form}>
      <Grid.Row style={{ paddingTop: '0px' }}>
        <Grid.Column>
          <InformationsNaissance
            numeroPartenaire={numeroPartenaire}
            partenaire={partenaire}
            errors={errorsStep}
            naissanceObligatoire={!isProjet}
            onChange={onChangePartenaire}
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column>
          <InformationsContact
            numeroPartenaire={numeroPartenaire}
            tel1={tel1}
            tel2={tel2}
            mail={mail}
            adresseResidence={adresseResidence}
            residenceCommuneDelegue={residenceCommuneDelegue}
            residenceLibelleCommuneDelegue={residenceLibelleCommuneDelegue}
            errors={errorsStep}
            onChange={onChangePartenaire}
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column>
          <InformationsComplementaires
            numeroPartenaire={numeroPartenaire}
            dejaPacse={dejaPacse}
            etatMatrimonialCode={etatMatrimonialCode}
            protectionOfpra={protectionOfpra}
            protectionJuridique={protectionJuridique}
            identiteVerifiee={identiteVerifiee}
            nombreEnfants={nombreEnfants}
            dateDivorce={numeroPartenaire === 1 ? p1DateDivorce : p2DateDivorce}
            dateVeuvage={numeroPartenaire === 1 ? p1DateVeuvage : p2DateVeuvage}
            inseeObligatoire={!isProjet}
            identiteVerifieeObligatoire={!isProjet}
            onChange={onChangePartenaire}
            errors={errorsStep}
          />
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export default StepPartenaire;
